import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import EditorSidebar from '@/components/EditorSidebar';
import { useIsMobile, useIsTablet } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, Plus, Eye, Trash2, Calendar } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useImageUpload } from '@/hooks/useImageUpload';
import PDFViewer from '@/components/PDFViewer';

interface OfficialDocument {
  id: string;
  title: string;
  year: number;
  description: string;
  document_url: string;
  created_at: string;
  created_by: string;
}

const EditorTextesOfficiels = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const [documents, setDocuments] = useState<OfficialDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    year: new Date().getFullYear(),
    description: '',
    document: null as File | null
  });
  const { uploadImage, uploading } = useImageUpload();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchDocuments();
  }, [user, navigate]);

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('official_documents')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des documents:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les documents',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.year || !formData.description || !formData.document) {
      toast({
        title: 'Erreur',
        description: 'Tous les champs sont obligatoires',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Upload du document PDF
      const documentUrl = await uploadImage(formData.document, 'documents');
      if (!documentUrl) {
        throw new Error('Erreur lors du téléchargement du document');
      }

      const { error } = await supabase
        .from('official_documents')
        .insert({
          title: formData.title,
          year: formData.year,
          description: formData.description,
          document_url: documentUrl,
          created_by: user?.id
        });

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'Document ajouté avec succès'
      });

      setIsDialogOpen(false);
      setFormData({
        title: '',
        year: new Date().getFullYear(),
        description: '',
        document: null
      });
      fetchDocuments();
    } catch (error) {
      console.error('Erreur lors de l\'ajout du document:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible d\'ajouter le document',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;

    try {
      const { error } = await supabase
        .from('official_documents')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'Document supprimé avec succès'
      });
      fetchDocuments();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer le document',
        variant: 'destructive'
      });
    }
  };

  // Render mobile layout
  if (isMobile) {
    return (
      <Layout>
        <div className="px-[25px] py-[50px] pb-20">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-primary">
              Gestion des textes officiels
            </h1>
            <p className="text-gray-600 mt-2 text-sm">Textes relatifs à la P49 et l'administration ivoirienne</p>
          </div>

          <div className="mb-4">
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="w-full bg-primary hover:bg-primary/90">
                  <Plus className="mr-2 h-4 w-4" />
                  Nouveau document
                </Button>
              </DialogTrigger>
              <DialogContent className="w-[calc(100vw-50px)] max-w-md mx-auto rounded-xl">
                <DialogHeader>
                  <DialogTitle>Ajouter un document</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Titre *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="year">Année *</Label>
                    <Input
                      id="year"
                      type="number"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="document">Document PDF *</Label>
                    <Input
                      id="document"
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setFormData({ ...formData, document: e.target.files?.[0] || null })}
                      required
                    />
                  </div>
                  <Button type="submit" disabled={uploading} className="w-full">
                    {uploading ? 'Téléchargement...' : 'Ajouter'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="mb-6">
            <h2 className="text-lg font-semibold text-primary flex items-center">
              <FileText className="mr-2 h-5 w-5" />
              Liste des textes officiels ({documents.length})
            </h2>
          </div>

          <div className="space-y-4">
            {loading ? (
              <div>Chargement...</div>
            ) : documents.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Aucun document trouvé</p>
            ) : (
              documents.map((doc) => (
                <Card key={doc.id}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center justify-between">
                      <span className="flex items-center">
                        <FileText className="mr-2 h-5 w-5" />
                        {doc.title}
                      </span>
                      <Badge variant="secondary">{doc.year}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-3">{doc.description}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500 flex items-center">
                        <Calendar className="mr-1 h-3 w-3" />
                        {new Date(doc.created_at).toLocaleDateString()}
                      </span>
                      <div className="flex space-x-2">
                        <PDFViewer
                          pdfUrl={doc.document_url}
                          title={doc.title}
                          triggerButton={
                            <Button size="sm" variant="outline">
                              <Eye className="h-4 w-4" />
                            </Button>
                          }
                        />
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleDelete(doc.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
        <EditorSidebar />
      </Layout>
    );
  }

  // Render tablet layout
  if (isTablet) {
    return (
      <Layout>
        <div className="px-[30px] py-[40px] pb-20">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-primary">Gestion des textes officiels</h1>
            <p className="text-gray-600 mt-2">Textes relatifs à la P49 et l'administration ivoirienne</p>
          </div>

          <div className="mb-6">
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90">
                  <Plus className="mr-2 h-4 w-4" />
                  Nouveau document
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Ajouter un document</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Titre *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="year">Année *</Label>
                    <Input
                      id="year"
                      type="number"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="document">Document PDF *</Label>
                    <Input
                      id="document"
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setFormData({ ...formData, document: e.target.files?.[0] || null })}
                      required
                    />
                  </div>
                  <Button type="submit" disabled={uploading} className="w-full">
                    {uploading ? 'Téléchargement...' : 'Ajouter'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-primary flex items-center">
              <FileText className="mr-2 h-5 w-5" />
              Liste des textes officiels ({documents.length})
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {loading ? (
              <p className="text-center py-8 col-span-full">Chargement...</p>
            ) : documents.length === 0 ? (
              <p className="text-gray-500 text-center py-8 col-span-full">Aucun document trouvé</p>
            ) : (
              documents.map((doc) => (
                <Card key={doc.id}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center justify-between">
                      <span className="flex items-center">
                        <FileText className="mr-2 h-5 w-5" />
                        {doc.title}
                      </span>
                      <Badge variant="secondary">{doc.year}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-3">{doc.description}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500 flex items-center">
                        <Calendar className="mr-1 h-3 w-3" />
                        {new Date(doc.created_at).toLocaleDateString()}
                      </span>
                      <div className="flex space-x-2">
                        <PDFViewer
                          pdfUrl={doc.document_url}
                          title={doc.title}
                          triggerButton={
                            <Button size="sm" variant="outline">
                              <Eye className="h-4 w-4" />
                            </Button>
                          }
                        />
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleDelete(doc.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
        <EditorSidebar />
      </Layout>
    );
  }

  // Render desktop layout
  return (
    <Layout>
      <div className="flex">
        <EditorSidebar />
        
        <div className="flex-1 ml-64 p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-primary">Gestion des textes officiels</h1>
            <p className="text-gray-600 mt-2">Textes relatifs à la P49 et l'administration ivoirienne</p>
          </div>

          <div className="mb-6">
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90">
                  <Plus className="mr-2 h-4 w-4" />
                  Nouveau document
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Ajouter un document</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Titre *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="year">Année *</Label>
                    <Input
                      id="year"
                      type="number"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="document">Document PDF *</Label>
                    <Input
                      id="document"
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setFormData({ ...formData, document: e.target.files?.[0] || null })}
                      required
                    />
                  </div>
                  <Button type="submit" disabled={uploading} className="w-full">
                    {uploading ? 'Téléchargement...' : 'Ajouter'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-primary flex items-center">
              <FileText className="mr-2 h-5 w-5" />
              Liste des textes officiels ({documents.length})
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {loading ? (
              <p className="text-center py-8 col-span-full">Chargement...</p>
            ) : documents.length === 0 ? (
              <p className="text-gray-500 text-center py-8 col-span-full">Aucun document trouvé</p>
            ) : (
              documents.map((doc) => (
                <Card key={doc.id}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center justify-between">
                      <span className="flex items-center">
                        <FileText className="mr-2 h-5 w-5" />
                        {doc.title}
                      </span>
                      <Badge variant="secondary">{doc.year}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-3">{doc.description}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500 flex items-center">
                        <Calendar className="mr-1 h-3 w-3" />
                        {new Date(doc.created_at).toLocaleDateString()}
                      </span>
                      <div className="flex space-x-2">
                        <PDFViewer
                          pdfUrl={doc.document_url}
                          title={doc.title}
                          triggerButton={
                            <Button size="sm" variant="outline">
                              <Eye className="h-4 w-4" />
                            </Button>
                          }
                        />
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleDelete(doc.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EditorTextesOfficiels;